import { useEffect, useState } from 'react';
import { FileText, AlertCircle, AlertTriangle, X, Check } from 'lucide-react';
import { Button, IconButton, NumberInput, Tag } from './ui';
import { confirmBundle } from '../hooks/useStatements';

export interface DetectedBoundary {
  start_page: number;
  end_page: number;
  bank_hint: string | null;
  period_hint: string | null;
  account_last4: string | null;
  confidence: number;
  fingerprint: string | null;
  is_new_format: boolean;
  /** Typed segmentation hint; null/"statement" = statement. */
  doc_type_hint?: string | null;
  /** The detected period range prints end before start. */
  period_reversed?: boolean;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  statement: 'Statement',
  tax_return: 'Tax return',
  pay_stub: 'Pay stub',
  legal_document: 'Legal filing',
  mortgage_statement: 'Mortgage statement',
  vehicle_valuation: 'Vehicle valuation',
  real_estate_valuation: 'Property valuation',
  mineral_lease: 'Oil & gas lease',
  royalty_statement: 'Royalty statement',
  division_order: 'Division order',
  other_non_financial: 'Non-financial',
  discovery_cover: 'Production cover',
  unknown: 'Unclassified',
  unsegmented: 'Needs review',
};

const isStatementHint = (hint: string | null | undefined) =>
  !hint || hint === 'statement';

const pageSpan = (r: { start_page: number; end_page: number }) =>
  r.end_page - r.start_page + 1;

/** Why this segment needs a human look, or null. Mirrors the server-side
 * guards: unsegmented remainders, low-confidence multi-page spans (the
 * 180-page conf-0 "cover" looked identical to a 1-page one), reversed
 * period prints. */
const rowWarning = (r: {
  start_page: number; end_page: number; confidence: number;
  doc_type_hint: string | null; period_reversed: boolean;
}): string | null => {
  if (r.doc_type_hint === 'unsegmented') {
    return 'These pages could not be segmented — check what they contain and fix the page ranges before approving.';
  }
  if (r.confidence === 0 && pageSpan(r) > 3) {
    return `No detection signals across ${pageSpan(r)} pages — verify this page range really is one document.`;
  }
  if (r.period_reversed) {
    return 'The statement period on this page prints end date before start date — verify the period.';
  }
  return null;
};

interface BundleReviewModalProps {
  open: boolean;
  bundleId: number | null;
  filename: string;
  pageCount: number;
  initialBoundaries: DetectedBoundary[];
  onClose: () => void;
  onConfirmed: (result: { statements_created: number; job_ids: number[] }) => void;
}

interface EditableRow {
  start_page: number;
  end_page: number;
  bank_hint: string | null;
  period_hint: string | null;
  account_last4: string | null;
  confidence: number;
  is_new_format: boolean;
  doc_type_hint: string | null;
  period_reversed: boolean;
  kept: boolean;
}

export default function BundleReviewModal({
  open, bundleId, filename, pageCount, initialBoundaries, onClose, onConfirmed,
}: BundleReviewModalProps) {
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Large productions produce dozens of segments; nobody audits 40 rows.
  // Show a grouped summary with one Approve, detail on demand.
  const [showDetail, setShowDetail] = useState(true);

  useEffect(() => {
    if (open) {
      setRows(initialBoundaries.map(b => ({
        start_page: b.start_page,
        end_page: b.end_page,
        bank_hint: b.bank_hint,
        period_hint: b.period_hint,
        account_last4: b.account_last4,
        confidence: b.confidence,
        is_new_format: b.is_new_format,
        doc_type_hint: b.doc_type_hint ?? null,
        period_reversed: b.period_reversed ?? false,
        kept: true,
      })));
      setErrorMsg(null);
      // Flagged rows must be visible: never collapse the detail table when
      // any segment carries a warning.
      const anyFlagged = initialBoundaries.some(b => rowWarning({
        start_page: b.start_page, end_page: b.end_page, confidence: b.confidence,
        doc_type_hint: b.doc_type_hint ?? null, period_reversed: b.period_reversed ?? false,
      }) !== null);
      setShowDetail(initialBoundaries.length <= 8 || anyFlagged);
    }
  }, [open, initialBoundaries]);

  if (!open) return null;

  const keptCount = rows.filter(r => r.kept).length;
  const nonStatementCount = rows.filter(r => !isStatementHint(r.doc_type_hint)).length;
  const flaggedCount = rows.filter(r => rowWarning(r) !== null).length;
  const summaryGroups: Array<[string, number]> = (() => {
    const m = new Map<string, number>();
    rows.forEach(r => {
      const label = isStatementHint(r.doc_type_hint)
        ? `${r.bank_hint || 'Statement'}${r.bank_hint ? ' statement' : ''}`
        : (DOC_TYPE_LABELS[r.doc_type_hint!] ?? r.doc_type_hint!);
      m.set(label, (m.get(label) ?? 0) + 1);
    });
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  })();

  const updateRow = (idx: number, patch: Partial<EditableRow>) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const submit = async (keptFor: (row: EditableRow, idx: number) => boolean) => {
    if (!bundleId) return;
    setErrorMsg(null);
    // Basic validation: every row must have start <= end, all within page count
    for (const [i, r] of rows.entries()) {
      if (r.start_page < 1 || r.end_page > pageCount || r.start_page > r.end_page) {
        setErrorMsg(`Row ${i + 1}: page range ${r.start_page}–${r.end_page} is invalid (PDF has ${pageCount} pages).`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const result = await confirmBundle(bundleId, rows.map((r, i) => ({
        start_page: r.start_page,
        end_page: r.end_page,
        kept: keptFor(r, i),
      })));
      onConfirmed({ statements_created: result.statements_created, job_ids: result.job_ids });
    } catch (err) {
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
      const msg = e?.response?.data?.detail || e?.message || 'Failed to confirm bundle.';
      setErrorMsg(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  // Use the page ranges + keep flags as shown.
  const handleConfirm = () => {
    if (keptCount === 0) {
      setErrorMsg('At least one statement must be kept.');
      return;
    }
    return submit(r => r.kept);
  };

  // "It's actually one statement" — keep only the first boundary so every page
  // merges back into a single statement. This is the one-click escape hatch when
  // the app proposed a split that shouldn't have happened.
  const handleKeepAsOne = () => submit((_r, i) => i === 0);

  return (
    <div className="fixed inset-0 z-(--z-modal) flex items-center justify-center bg-ink/40 p-4 animate-scrim">
      <div className="bg-surface-card rounded-overlay shadow-modal w-full max-w-4xl max-h-[90vh] flex flex-col border border-border animate-dialog">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border-subtle flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
              <FileText size={20} className="text-green-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="t-card text-green-900 truncate" title={filename}>
                {nonStatementCount > 0 ? 'Review detected documents' : 'Review detected statements'}
              </h3>
              <p className="t-caption mt-1">
                <span className="truncate inline-block max-w-72 align-bottom" title={filename}>{filename}</span>
                {' · '}{pageCount} pages · {rows.length} document{rows.length !== 1 ? 's' : ''} detected
                {nonStatementCount > 0 && ` (${rows.length - nonStatementCount} statement${rows.length - nonStatementCount !== 1 ? 's' : ''}, ${nonStatementCount} other)`}
              </p>
            </div>
          </div>
          <IconButton
            icon={X}
            size="sm"
            variant="ghost"
            aria-label="Cancel"
            onClick={onClose}
            disabled={submitting}
            className="text-ink-faint hover:bg-transparent hover:text-ink-soft"
          />
        </div>

        {/* Intro / explainer */}
        <div className="px-6 py-3 bg-cream border-b border-border-subtle text-sm text-ink-soft">
          {nonStatementCount > 0 ? (
            <>We detected {rows.length} separate documents in this PDF — statements plus other document types.
            {' '}Statements are parsed and reconciled; other types go to their own review lanes, and anything
            non-financial is kept in Documents without processing.
            {' '}Uncheck a row to merge it into the previous one, or edit page ranges if a boundary looks off, then approve.</>
          ) : (
            <>We detected what looks like {rows.length} separate statements in this PDF.
            {' '}<strong>If this is actually one statement</strong>, click <em>“It’s one statement”</em> below — splitting a single statement into pieces stops it from reconciling.
            {' '}Otherwise, uncheck any row that's actually part of the previous statement, or edit page ranges if a boundary looks off, then approve.</>
          )}
        </div>

        {/* Grouped summary — the level a person actually reviews at */}
        <div className="px-6 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            {summaryGroups.map(([label, n]) => (
              <Tag key={label} tone="good">
                {n > 1 ? `${n}× ` : ''}{label}
              </Tag>
            ))}
            {flaggedCount > 0 && (
              <Tag tone="warn" icon={AlertTriangle}>
                {flaggedCount} segment{flaggedCount !== 1 ? 's' : ''} need{flaggedCount === 1 ? 's' : ''} a look
              </Tag>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => setShowDetail(d => !d)}
            >
              {showDetail ? 'Hide page detail' : `Show page detail (${rows.length} segments)`}
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className={`flex-1 overflow-y-auto px-6 py-4 ${showDetail ? '' : 'hidden'}`}>
          {/* eslint-disable-next-line no-restricted-syntax -- editable review grid: per-row keep/range inputs don't fit DataTable; restyled in place */}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-2xs font-semibold uppercase tracking-eyebrow text-ink-faint border-b border-border">
                <th className="py-2 pr-3 w-10">Keep</th>
                <th className="py-2 pr-3 w-40">Pages</th>
                <th className="py-2 pr-3 w-28">Type</th>
                <th className="py-2 pr-3">Bank</th>
                <th className="py-2 pr-3">Account</th>
                <th className="py-2 pr-3">Period</th>
                <th className="py-2 pr-3 w-14" title="Detection score on the segment's first page">Conf.</th>
                <th className="py-2 pr-3 w-20">Format</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {rows.map((r, idx) => {
                const warning = rowWarning(r);
                return (
                <tr key={idx} className={`${r.kept ? '' : 'opacity-50'} ${warning ? 'bg-clay-50' : ''}`}>
                  <td className="py-3 pr-3">
                    {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
                    <input
                      type="checkbox"
                      checked={r.kept}
                      disabled={idx === 0 /* first row always kept */}
                      onChange={e => updateRow(idx, { kept: e.target.checked })}
                      className="w-4 h-4 rounded accent-green-700 cursor-pointer disabled:cursor-not-allowed"
                      title={idx === 0 ? 'The first statement cannot be removed' : 'Uncheck to merge into the previous statement'}
                    />
                  </td>
                  <td className="py-3 pr-3">
                    <div className="flex items-center gap-1 text-ink-soft">
                      <NumberInput
                        size="sm"
                        min={1}
                        max={pageCount}
                        value={r.start_page}
                        onChange={e => updateRow(idx, { start_page: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                        className="w-14 px-1 text-center text-xs"
                      />
                      <span className="text-ink-faint">–</span>
                      <NumberInput
                        size="sm"
                        min={1}
                        max={pageCount}
                        value={r.end_page}
                        onChange={e => updateRow(idx, { end_page: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                        className="w-14 px-1 text-center text-xs"
                      />
                      <span className="text-2xs text-ink-faint whitespace-nowrap">
                        {pageSpan(r)} pp
                      </span>
                    </div>
                  </td>
                  <td className="py-3 pr-3">
                    <div className="flex items-center gap-1">
                    {warning && (
                      <span title={warning} className="text-clay-700 flex-shrink-0 cursor-help">
                        <AlertTriangle size={12} />
                      </span>
                    )}
                    {isStatementHint(r.doc_type_hint) ? (
                      <Tag size="sm" tone="good">Statement</Tag>
                    ) : (
                      <Tag size="sm" tone="neutral">{DOC_TYPE_LABELS[r.doc_type_hint!] ?? r.doc_type_hint}</Tag>
                    )}
                    </div>
                  </td>
                  <td className="py-3 pr-3 text-ink">
                    {r.bank_hint || (
                      <span className="text-ink-faint italic">
                        {isStatementHint(r.doc_type_hint) ? 'Unknown' : '—'}
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-3 text-ink-soft">
                    {r.account_last4 ? <span className="t-ident">••{r.account_last4}</span> : <span className="text-ink-faint">—</span>}
                  </td>
                  <td className="py-3 pr-3 text-ink-soft">
                    {r.period_hint || <span className="text-ink-faint">—</span>}
                    {r.period_reversed && (
                      <span className="ml-1 text-2xs font-medium text-warn">(reversed)</span>
                    )}
                  </td>
                  <td className={`py-3 pr-3 text-xs ${r.confidence === 0 ? 'text-clay-700 font-medium' : 'text-ink-soft'}`}>
                    {r.confidence}
                  </td>
                  <td className="py-3 pr-3">
                    {r.is_new_format ? (
                      <Tag size="sm" tone="warn">NEW</Tag>
                    ) : (
                      <Tag size="sm" tone="good">Known</Tag>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Error banner */}
        {errorMsg && (
          <div className="mx-6 mb-3 flex items-start gap-2 px-3 py-2 bg-danger-soft border border-danger rounded-lg text-sm text-danger">
            <AlertCircle size={16} className="flex-shrink-0 mt-px" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border-subtle flex items-center justify-between gap-3 bg-cream">
          <p className="text-xs text-ink-faint">
            {keptCount} document{keptCount !== 1 ? 's' : ''} will be queued for processing.
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
            {rows.length > 1 && (
              <Button variant="secondary" onClick={handleKeepAsOne} disabled={submitting}>
                It’s one statement
              </Button>
            )}
            <Button onClick={handleConfirm} disabled={keptCount === 0} loading={submitting} icon={Check}>
              {submitting ? 'Processing…' : `Approve and process ${keptCount}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
